import { memo, useEffect } from 'react';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import * as yup from 'yup';

import FormActionButtons from 'components/ui/form/FormActionButtons';
import FormCurrencyField from 'components/ui/form/FormCurrencyField';
import FormSelectField from 'components/ui/form/FormSelectField';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useFormSubmit from 'hooks/useFormSubmit';
import formLocalizedHelperText from 'utils/formLocalizedHelperText';

const baseShape = {
  type_id: yup.number().required('settings.staffKpi.validations.typeId'),
  count_type: yup.number().required('settings.staffKpi.validations.countType'),
  price: yup
    .number()
    .typeError('settings.staffKpi.validations.price')
    .required('settings.staffKpi.validations.price'),
};

// Qo'shishda — bir nechta xodim, tahrirlashda — bitta
const addSchema = yup.object({
  ...baseShape,
  user_ids: yup.array().min(1, 'settings.staffKpi.validations.userId'),
});
const editSchema = yup.object({
  ...baseShape,
  user_id: yup.number().required('settings.staffKpi.validations.userId'),
});

const AddModal = (props) => {
  const { item, setItem, open, setOpen, setRefetch } = props;
  const { t } = useTranslation();
  const { submit, isSubmitting } = useFormSubmit();
  const axiosPrivate = useAxiosPrivate();

  // Xodimlar ro'yxati — qidiruvli Autocomplete uchun
  const { data: staffList } = useQuery({
    queryKey: '/dictionary/staffes',
    queryFn: async () =>
      (await axiosPrivate.get('/dictionary/staffes')).data?.data || [],
    retry: false,
    staleTime: 60000,
  });
  const staffOptions = staffList || [];

  const formik = useFormik({
    initialValues: {
      user_id: '',
      user_ids: [],
      type_id: '',
      count_type: '',
      price: '',
      // Progressiv oraliqlar: [{from_count, to_count, count_type, price}]
      tiers: [],
    },
    validationSchema: item?.id ? editSchema : addSchema,
    onSubmit: async (values) => {
      const method = item?.id ? 'postUpdate' : 'post';
      const message = item?.id
        ? 'settings.staffKpi.alerts.updated'
        : 'settings.staffKpi.alerts.added';
      submit(
        { type: method, contentType: 'simple' },
        values,
        '/admin/staff-kpi',
        { title: t(message) },
        item?.id,
        false,
        handleFinish
      );
    },
  });

  const handleFinish = () => {
    setRefetch(true);
    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
    setItem(null);
    formik.resetForm();
  };

  useEffect(() => {
    if (item?.id) {
      formik.setValues({
        user_id: item.user_id || '',
        user_ids: [],
        type_id: item.type_id || '',
        count_type: item.count_type || '',
        price: item.price || '',
        tiers: (item.tiers || []).map((tier) => ({
          from_count: tier.from_count ?? '',
          to_count: tier.to_count ?? '',
          count_type: tier.count_type || 2,
          price: tier.price ?? '',
        })),
      });
    }
  }, [item]);

  const tiers = formik.values.tiers || [];

  const addTier = () => {
    const last = tiers[tiers.length - 1];
    formik.setFieldValue('tiers', [
      ...tiers,
      {
        // keyingi oraliq avvalgisining davomidan boshlanadi
        from_count:
          last && last.to_count !== '' && last.to_count !== null
            ? Number(last.to_count) + 1
            : tiers.length === 0
            ? 1
            : '',
        to_count: '',
        count_type: last?.count_type || 2,
        price: '',
      },
    ]);
  };

  const removeTier = (idx) => {
    formik.setFieldValue(
      'tiers',
      tiers.filter((_, i) => i !== idx)
    );
  };

  const changeTier = (idx, key, value) => {
    const next = tiers.map((tier, i) =>
      i === idx ? { ...tier, [key]: value } : tier
    );
    formik.setFieldValue('tiers', next);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{ className: '!base-border !shadow-none' }}
      disableEscapeKeyDown
    >
      <DialogTitle className='!bg-gray-100 !mb-2 text-center'>
        <span className='pr-5'>
          {t(`settings.staffKpi.${item?.id ? 'editModal' : 'addModal'}.title`)}
        </span>
      </DialogTitle>

      <DialogContent>
        <form onSubmit={formik.handleSubmit} className='pt-2'>
          <Grid container spacing={{ xs: 2, sm: 2 }} rowSpacing={1} columns={{ xs: 12, sm: 12 }}>
            <Grid item={true} sm={12} xs={12}>
              {item?.id ? (
                /* Tahrirlash — bitta xodim (qidiruvli) */
                <Autocomplete
                  options={staffOptions}
                  getOptionLabel={(o) => o?.name || ''}
                  isOptionEqualToValue={(o, v) => o?.id === v?.id}
                  value={
                    staffOptions.find((s) => s.id === formik.values.user_id) ||
                    null
                  }
                  onChange={(e, v) =>
                    formik.setFieldValue('user_id', v?.id ?? '')
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('common.fields.staff')}
                      error={
                        formik.touched.user_id &&
                        Boolean(formik.errors.user_id)
                      }
                      helperText={
                        formik.touched.user_id &&
                        formLocalizedHelperText(formik.errors.user_id)
                      }
                    />
                  )}
                />
              ) : (
                /* Qo'shish — bir nechta xodim birdan (qidiruvli, maks 20 ta) */
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={staffOptions}
                  getOptionLabel={(o) => o?.name || ''}
                  isOptionEqualToValue={(o, v) => o?.id === v?.id}
                  getOptionDisabled={() =>
                    (formik.values.user_ids || []).length >= 20
                  }
                  value={staffOptions.filter((s) =>
                    (formik.values.user_ids || []).includes(s.id)
                  )}
                  onChange={(e, v) =>
                    formik.setFieldValue(
                      'user_ids',
                      v.slice(0, 20).map((x) => x.id)
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('common.fields.staff')}
                      placeholder={t('common.global.search')}
                      error={
                        formik.touched.user_ids &&
                        Boolean(formik.errors.user_ids)
                      }
                      helperText={
                        formik.touched.user_ids &&
                        formLocalizedHelperText(formik.errors.user_ids)
                      }
                    />
                  )}
                />
              )}
            </Grid>

            <Grid item={true} sm={12} xs={12}>
              <FormSelectField
                delay={0.1}
                fieldName='type_id'
                label={t('common.fields.type')}
                formik={formik}
                path='/dictionary/kpitypes'
                isitemsizesmall
              />
            </Grid>

            <Grid item={true} sm={6} xs={12}>
              <FormControl
                fullWidth
                error={formik.touched.count_type && Boolean(formik.errors.count_type)}
              >
                <InputLabel>{t('settings.staffKpi.fields.countType')}</InputLabel>
                <Select
                  name='count_type'
                  label={t('settings.staffKpi.fields.countType')}
                  value={formik.values.count_type}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <MenuItem value={1}>{t('settings.staffKpi.countType.procent')}</MenuItem>
                  <MenuItem value={2}>{t('settings.staffKpi.countType.sum')}</MenuItem>
                </Select>
                {formik.touched.count_type && formik.errors.count_type && (
                  <FormHelperText>
                    {formLocalizedHelperText(formik.errors.count_type)}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item={true} sm={6} xs={12}>
              <FormCurrencyField
                delay={0.2}
                label={t('settings.staffKpi.fields.price')}
                fieldName='price'
                formik={formik}
                decimalScale={5}
              />
            </Grid>

            {/* Progressiv oraliqlar: 1-5 ta => stavka1, 6-10 ta => stavka2 ... */}
            <Grid item={true} sm={12} xs={12}>
              <div className='border border-gray-200 rounded-lg p-3'>
                <div className='flex items-center justify-between'>
                  <span className='text-[13px] font-medium text-gray-600'>
                    {t('settings.staffKpi.tiers.title')}
                  </span>
                  <Button
                    size='small'
                    variant='outlined'
                    onClick={addTier}
                    startIcon={<i className='bi bi-plus' />}
                  >
                    {t('settings.staffKpi.tiers.add')}
                  </Button>
                </div>

                {tiers.length > 0 && (
                  <>
                    {tiers.map((tier, idx) => (
                      <div
                        key={`tier-${idx}`}
                        className='flex items-center gap-2 mt-2'
                      >
                        <TextField
                          size='small'
                          type='number'
                          label={t('settings.staffKpi.tiers.from')}
                          value={tier.from_count}
                          onChange={(e) =>
                            changeTier(idx, 'from_count', e.target.value)
                          }
                          className='!w-[85px]'
                        />
                        <TextField
                          size='small'
                          type='number'
                          label={t('settings.staffKpi.tiers.to')}
                          placeholder='∞'
                          value={tier.to_count}
                          onChange={(e) =>
                            changeTier(idx, 'to_count', e.target.value)
                          }
                          className='!w-[95px]'
                        />
                        <Select
                          size='small'
                          value={tier.count_type}
                          onChange={(e) =>
                            changeTier(idx, 'count_type', e.target.value)
                          }
                          className='!w-[110px]'
                        >
                          <MenuItem value={1}>
                            {t('settings.staffKpi.countType.procent')}
                          </MenuItem>
                          <MenuItem value={2}>
                            {t('settings.staffKpi.countType.sum')}
                          </MenuItem>
                        </Select>
                        <TextField
                          size='small'
                          type='number'
                          label={t('settings.staffKpi.fields.price')}
                          value={tier.price}
                          onChange={(e) =>
                            changeTier(idx, 'price', e.target.value)
                          }
                          className='flex-1'
                          inputProps={{ step: 'any' }}
                        />
                        <IconButton size='small' onClick={() => removeTier(idx)}>
                          <i className='bi bi-x text-red-500' />
                        </IconButton>
                      </div>
                    ))}
                    <p className='text-[11px] text-gray-400 mt-2 mb-0'>
                      {t('settings.staffKpi.tiers.hint')}
                    </p>
                  </>
                )}
              </div>
            </Grid>

            <Grid item={true} sm={12} xs={12}>
              <FormActionButtons
                delay={0.3}
                isSubmitting={isSubmitting}
                formType='dialog'
                setOpen={setOpen}
                reset={formik.resetForm}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default memo(AddModal);
